import React, { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = 'pwa_install_dismissed';

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export const InstallPWA = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    const onInstalled = () => { setVisible(false); setDeferred(null); };
    window.addEventListener('appinstalled', onInstalled);

    // iOS não dispara beforeinstallprompt — mostra dica manual no Safari
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 1500);
      setIosHint(true);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => { setVisible(false); localStorage.setItem(DISMISS_KEY, '1'); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* noop */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-[60] bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white shadow-lg p-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <img src="/logo192.png" alt="Smart Flow" className="h-11 w-11 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 leading-tight">Instalar Smart Flow</p>
          {iosHint ? (
            <p className="text-xs text-slate-500 leading-snug flex items-center gap-1 mt-0.5">
              Toque em <Share className="inline h-3.5 w-3.5" /> e depois em “Adicionar à Tela de Início”.
            </p>
          ) : (
            <p className="text-xs text-slate-500 leading-snug mt-0.5">Acesse como app, direto da tela inicial.</p>
          )}
        </div>
        {!iosHint && (
          <Button onClick={install} size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1.5 flex-shrink-0" data-testid="pwa-install-button">
            <Download className="h-4 w-4" /> Instalar
          </Button>
        )}
        <button onClick={dismiss} className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0" aria-label="Fechar" data-testid="pwa-install-dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
